import React from 'react';
import styled, { css } from 'styled-components';
import { useCombobox } from 'downshift';

const Label = styled.label(
  ({ theme }) => css`
    font-size: 1rem;

    @media (max-width: 630px) {
      font-size: 1.125rem;
    }
  `
);

const Fieldset = styled.fieldset(
  () => css`
    position: relative;
    width: 100%;
    outline: none;
    border: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  `
);

const Input = styled.input(
  ({ theme }) => css`
    width: 100%;
    padding: 0.75rem;
    background: ${theme.paper.default};
    outline: none;
    caret-color: ${theme.text.default};
    color: ${theme.text.default};
    border: none;
    border-radius: 0.5rem;
    font-size: 1rem;
  `
);

const ComboboxList = styled.ul(
  ({ theme }) => css`
    position: absolute;
    top: 110%;
    left: 0;
    right: 0;
    border: 1px solid ${theme.border.default};
    background: ${theme.paper.default};
    color: ${theme.text.default};
    z-index: 99;
    list-style: none;
    margin: 0;
    padding: 0.5rem 0;
    max-height: 300px;
    overflow-y: auto;
    border-radius: 0.5rem;
    scrollbar-width: thin;
    scrollbar-color: ${theme.paper.modal} ${theme.paper.default};
  `
);

interface ListItemProps {
  $active: boolean;
}

const ListItem = styled.li<ListItemProps>(
  ({ theme, $active }) => css`
    padding: 0.5rem;
    cursor: pointer;
    background: ${$active ? theme.paper.hover : theme.paper.default};
    font-size: 1rem;

    &:focus {
      outline: none;
      background: ${theme.paper.hover};
      font-weight: 500;
    }
  `
);

interface ComboBoxProps {
  label: string;
  options: string[];
  onSelect: (value: string) => void;
  placeholder?: string;
}

export function ComboBox({
  label,
  options,
  onSelect,
  placeholder,
}: ComboBoxProps) {
  const [inputItems, setInputItems] = React.useState(options);
  const {
    isOpen,
    getLabelProps,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
    inputValue,
  } = useCombobox({
    items: inputItems,
    onInputValueChange: ({ inputValue }) => {
      setInputItems(
        options.filter((item) =>
          item.toLowerCase().startsWith(inputValue.toLowerCase())
        )
      );
      if (!options.includes(inputValue)) return;

      onSelect(inputValue);
    },
  });

  const showDropdown = isOpen && inputValue && inputItems.length > 0;

  return (
    <Fieldset>
      <Label {...getLabelProps()} htmlFor="combobox">
        {label}
      </Label>
      <Input {...getInputProps()} placeholder={placeholder} />

      {showDropdown && (
        <ComboboxList {...getMenuProps()}>
          {inputItems.map((item, index) => (
            <ListItem
              $active={highlightedIndex === index}
              key={`${item}${index}`}
              {...getItemProps({ item, index })}
            >
              {item}
            </ListItem>
          ))}
        </ComboboxList>
      )}
    </Fieldset>
  );
}
